import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui-custom/Tabs";
import { FileSpreadsheet, Activity, BarChart3, Loader2, RefreshCw } from "lucide-react";
import BoQSetup from "./BoQSetup";
import BoQExecutionTracking from "./BoQExecutionTracking";
import BoQAnalytics from "./BoQAnalytics";
import { toast } from "sonner";
import { BoQApi } from "../../../api/boq/service";
import { normalizeBoQSection } from "./boqData";
import Button from "components/ui-custom/Button";
import LoadingScreen from "components/UI_components/LoadingScreen";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";

const BoQ = ({userPermissions, permissionsLoading}) => {
  const [activeTab, setActiveTab] = useState("setup");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingTasks, setGeneratingTasks] = useState(false);

  const loadBoQ = async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const sectionData = await BoQApi.getSectionsWithItems();
      setSections(sectionData.map((section, index) => normalizeBoQSection(section, section.items, index)));
    } catch (error) {
      console.error("Failed to load BoQ data", error);
      toast.error(error.message || "Failed to load BoQ data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadBoQ();
  }, []);

  const handleGenerateTasks = async () => {
    try {
      setGeneratingTasks(true);
      const response = await BoQApi.generateTasks();
      toast.success(response?.message || "Task structure generated successfully");
      await loadBoQ({ silent: true });
    } catch (error) {
      console.error("Failed to generate BoQ tasks", error);
      toast.error(error.message || "Failed to generate task structure");
    } finally {
      setGeneratingTasks(false);
    }
  };
  if (
    !userPermissions?.is_admin &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module={"BoQ"}
        description={
          "You don't have permission to view Bill of Quantites (BoQ) information for this site. Please contact your administrator for access. "
        }
      />
    );
  }
  return (
    <LoadingScreen loading={loading}>
      <div className="space-y-4 w-full relative py-4 px-4">
        <div className="flex items-center justify-between">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-9 p-1">
              <TabsTrigger value="setup" className="h-7 text-sm px-4 gap-1.5">
                <FileSpreadsheet className="h-3.5 w-3.5" /> Setup
              </TabsTrigger>
              <TabsTrigger value="execution" className="h-7 text-sm px-4 gap-1.5">
                <Activity className="h-3.5 w-3.5" /> Execution
              </TabsTrigger>
              <TabsTrigger value="analytics" className="h-7 text-sm px-4 gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" /> Analytics
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-sm gap-1.5 border-border/60"
            onClick={loadBoQ}
            disabled={refreshing}
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>

        {!loading &&
          (activeTab === "setup" ? (
            <BoQSetup
              sections={sections}
              onRefresh={() => loadBoQ({ silent: true })}
              onGenerateTasks={handleGenerateTasks}
              generatingTasks={generatingTasks}
              refreshing={refreshing}
            />
          ) : activeTab === "execution" ? (
            <BoQExecutionTracking sections={sections} refreshing={refreshing} />
          ) : (
            <BoQAnalytics sections={sections} refreshing={refreshing} />
          ))}
      </div>
    </LoadingScreen>
  );
};

export default BoQ;
